import {
	ButtonBase,
	Dialog,
	TableView,
	Toolbar
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	PropsWithChildren,
	ReactElement,
	useCallback,
	useState
} from "react";
import {
	AnnouncementPinModel,
	unPin
} from "client/Api/Announcements/announcements";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import styles from "./PinnedAnnouncementsDialog.module.less";
import { announcementStatusToLocalizationKeyMap } from "../../HubManagementDialog/Content/AnnouncementsContent/AnnouncementsOverview";
import { resolveFormattedTextString } from "client/Misc/utils";

export type PinnedAnnouncementsDialogProps = {
	pinnedItems: readonly AnnouncementPinModel[];
	onClose(wasUpdated?: boolean): void;
	categoryName: string;
};

const {
	Cell,
	Row
} = TableView;

export const PinnedAnnouncementsDialog = (props: PinnedAnnouncementsDialogProps): ReactElement => {
	const {
		pinnedItems,
		categoryName,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [unPinnedItems, setUnPinnedItems] = useState<Set<number>>(new Set());
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const canBeSaved: boolean = unPinnedItems.size > 0;

	const togglePinStatus = useCallback(
		(item: AnnouncementPinModel): void => {
			setUnPinnedItems(
				unPinnedItems => {
					const copy: Set<number> = new Set(unPinnedItems);
					const isPinned: boolean = !copy.has(item.id);

					if(isPinned) {
						copy.add(item.id);
					} else {
						copy.delete(item.id);
					}

					return copy;
				}
			);
		},
		[]
	);

	const saveHandler = async(): Promise<void> => {
		setIsBusy(true);

		try {
			await unPin(
				Array.from(unPinnedItems),
				globalCancellation
			);

			setIsBusy(false);
			onClose(true);
		} catch(e) {
			if (isCancelledError(e)) {
				return;
			}

			setIsBusy(false);
			alertManager.showError(localize("_GeneralErrorMessage"));
			console.error(e);
		}
	};

	const tableHeader: string = resolveFormattedTextString({
		template: localize("_PinnedAnnouncementsForName"),
		replacements: [{
			what: "{{Name}}",
			filler: categoryName
		}]
	});

	const dialogHeader: string = resolveFormattedTextString({
		template: localize("_NoMoreItemsCanBePinnedInTheCategoryName"),
		replacements: [{
			what: "{{Name}}",
			filler: categoryName
		}]
	});

	return(
		<Dialog
			trackingId="pinned-announcements-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={dialogHeader}
				onClose={onClose}
			/>
			<Dialog.Body>
				<p>{localize("_TheMaximumNumberOfPinnedItemsPerCategory")}</p>
				<h4
					className={styles.title}
					title={tableHeader}
				>
					{tableHeader}
				</h4>
				<TableView
					trackingId="pinned-announcements"
					items={pinnedItems}
					itemKey="id"
					columns={[
						{
							title: localize("_Title"),
							trackingId: "title",
							width: "fill"
						},
						{
							title: localize("_Status"),
							trackingId: "status",
							width: "fill"
						},
						{
							title: localize("_Action"),
							trackingId: "action",
							width: "fill"
						}
					]}
					hasZebraPrinting
				>
					{x => (
						<Row
							trackingId={x.id.toString()}
						>
							<Cell>{x.title}</Cell>
							<Cell>{localize(announcementStatusToLocalizationKeyMap[x.status])}</Cell>
							<Cell>
								<ButtonBase
									onClick={() => togglePinStatus(x)}
									trackingId={unPinnedItems.has(x.id)
										? "pin"
										: "unpin"
									}
									className={styles.button}
								>
									{localize(unPinnedItems.has(x.id)
										? "_Pin"
										: "_Unpin"
									)}
								</ButtonBase>
							</Cell>
						</Row>
					)}
				</TableView>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={saveHandler}
						text={localize("_Save")}
						trackingId="save"
						isDisabled={!canBeSaved}
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
PinnedAnnouncementsDialog.displayName = "PinnedAnnouncementsDialog";

export type PinnedAnnouncementsDialogInitialData = Omit<
	PropsWithChildren<PinnedAnnouncementsDialogProps>,
	"onClose"
>;

export function usePinnedAnnouncementsDialog(): [
	element: ReactElement | null,
	open: (props: PinnedAnnouncementsDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => {
			const onCloseHandler = (wasUpdated?: boolean): void => {
				if (wasUpdated === true) {
					onSave();
				} else {
					onClose();
				}
			};

			return (
				<PinnedAnnouncementsDialog
					{...props}
					onClose={onCloseHandler}
				/>
			);
		}
	);
}